import React from 'react';

import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';

import RichTextContent from 'components/RichTextContent/RichTextContent';
import { translatedOrFallback } from 'helpers/safeT/safeT';
import action_library_messages from 'messages/action_library_messages';

import { ActionLibraryInstanceFormValues } from '../../ActionLibraryInstanceForm.types';

import useStyles from './ActionLibraryInstanceSummaryDialog.styles';

type Props = {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  values: ActionLibraryInstanceFormValues;
  isSubmitting: boolean;
  excludedAssetNames?: string | null;
};

const ActionLibraryInstanceSummaryDialog: React.FC<Props> = ({ open, onClose, onConfirm, values, isSubmitting, excludedAssetNames }) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const template = values.selectedTemplate;

  const formatDate = (dateValue: string | Date | null | undefined) => {
    if (!dateValue) return '-';

    let date: Date;

    if (dateValue instanceof Date) {
      date = dateValue;
    } else if (typeof dateValue === 'string') {
      // If already in YYYY-MM-DD format, return as-is
      if (dateValue.match(/^\d{4}-\d{2}-\d{2}$/)) {
        return dateValue;
      }

      // Try parsing the date string
      date = new Date(dateValue);

      // Check if date is valid
      if (Number.isNaN(date.getTime())) {
        return '-';
      }
    } else {
      return '-';
    }

    // Format as YYYY-MM-DD using date-fns (same as parseDate helper)
    try {
      return format(date, 'yyyy-MM-dd');
    } catch (e) {
      return '-';
    }
  };

  const getStepStatus = (stepId: number, originalMandatory?: boolean) => {
    const override = values.stepOverrides.find(o => o.originalStepId === stepId);
    const isMandatory = override ? override.mandatory : !!originalMandatory;
    return isMandatory
      ? t(...(action_library_messages.instance.summary_dialog.mandatory as [string, string]))
      : t(...(action_library_messages.instance.summary_dialog.optional as [string, string]));
  };

  const includedSteps = (template?.steps || []).filter(step => {
    const o = values.stepOverrides.find(r => r.originalStepId === step.id);
    return o == null || o.included;
  });

  const label = (msg: string) => {
    const s = String(msg).trimEnd();
    if (!s) return '';
    return s.endsWith(':') ? s : `${s}:`;
  };

  return (
    <Dialog fullWidth maxWidth='md' onClose={onClose} open={open}>
      <DialogTitle>{t(...(action_library_messages.instance.summary_dialog.title as [string, string]))}</DialogTitle>
      <DialogContent dividers>
        {excludedAssetNames && (
          <Alert severity='warning' style={{ marginBottom: 16 }}>
            {t(action_library_messages.instance.bulk_create.summary_dialog_excluded_assets, {
              assetNames: excludedAssetNames,
            })}
          </Alert>
        )}
        <Box className={classes.section}>
          <Typography className={classes.sectionTitle} variant='subtitle1'>
            {t(...(action_library_messages.instance.summary_dialog.general_info as [string, string]))}
          </Typography>
          <div className={classes.row}>
            <Typography className={classes.label} variant='body2'>
              {label(t(...(action_library_messages.instance.summary_dialog.template as [string, string])))}
            </Typography>
            <Typography className={classes.value} variant='body2'>
              {template?.name || '-'}
            </Typography>
          </div>
          {template && (
            <>
              <div className={classes.row}>
                <Typography className={classes.label} variant='body2'>
                  {label(t(...(action_library_messages.instance.summary_dialog.area as [string, string])))}
                </Typography>
                <Typography className={classes.value} variant='body2'>
                  {template.areaDisplay}
                  {template.subAreaData
                    ? ` / ${translatedOrFallback(t, template.subAreaData.nameTranslationKey, template.subAreaData.name)}`
                    : ''}
                </Typography>
              </div>
              <div className={classes.row}>
                <Typography className={classes.label} variant='body2'>
                  {label(t(...(action_library_messages.instance.summary_dialog.criticality as [string, string])))}
                </Typography>
                <Typography className={classes.value} variant='body2'>
                  {template.criticality
                    ? t(
                        action_library_messages.criticality[
                          template.criticality.toUpperCase() as keyof typeof action_library_messages.criticality
                        ],
                      )
                    : template.criticalityDisplay || '-'}
                </Typography>
              </div>
              {template.reference != null && template.reference !== '' && (
                <div className={classes.row}>
                  <Typography className={classes.label} variant='body2'>
                    {label(t(...(action_library_messages.instance.summary_dialog.reference as [string, string])))}
                  </Typography>
                  <Typography className={classes.value} variant='body2'>
                    {template.reference}
                  </Typography>
                </div>
              )}
              {template.reportedTo != null && template.reportedTo !== '' && (
                <div className={classes.row}>
                  <Typography className={classes.label} variant='body2'>
                    {label(t(...(action_library_messages.instance.summary_dialog.reported_to as [string, string])))}
                  </Typography>
                  <Typography className={classes.value} variant='body2'>
                    {template.reportedTo}
                  </Typography>
                </div>
              )}
              {template.validityPeriodMonths != null && (
                <div className={classes.row}>
                  <Typography className={classes.label} variant='body2'>
                    {label(t(...(action_library_messages.instance.summary_dialog.validity_period as [string, string])))}
                  </Typography>
                  <Typography className={classes.value} variant='body2'>
                    {template.validityPeriodMonths}{' '}
                    {template.validityPeriodMonths === 1
                      ? t(...(action_library_messages.instance.form.month as [string, string]))
                      : t(...(action_library_messages.instance.form.months as [string, string]))}
                  </Typography>
                </div>
              )}
              {template.assetCategories && template.assetCategories.length > 0 && (
                <div className={classes.row}>
                  <Typography className={classes.label} variant='body2'>
                    {label(t(...(action_library_messages.instance.summary_dialog.asset_categories as [string, string])))}
                  </Typography>
                  <Typography className={classes.value} variant='body2'>
                    {template.assetCategories.map(cat => cat.name).join(', ')}
                  </Typography>
                </div>
              )}
              {template.ownerTypeDisplay != null && template.ownerTypeDisplay !== '' && (
                <div className={classes.row}>
                  <Typography className={classes.label} variant='body2'>
                    {label(t(...(action_library_messages.instance.summary_dialog.owner_type as [string, string])))}
                  </Typography>
                  <Typography className={classes.value} variant='body2'>
                    {template.ownerTypeDisplay}
                  </Typography>
                </div>
              )}
              {template.statusDisplay != null && template.statusDisplay !== '' && (
                <div className={classes.row}>
                  <Typography className={classes.label} variant='body2'>
                    {label(t(...(action_library_messages.instance.summary_dialog.status as [string, string])))}
                  </Typography>
                  <Typography className={classes.value} variant='body2'>
                    {template.statusDisplay}
                  </Typography>
                </div>
              )}
            </>
          )}
          <div className={classes.row}>
            <Typography className={classes.label} variant='body2'>
              {label(t(...(action_library_messages.instance.summary_dialog.instance_name as [string, string])))}
            </Typography>
            <Typography className={classes.value} variant='body2'>
              {values.instanceName || '-'}
            </Typography>
          </div>
          <div className={classes.row}>
            <Typography className={classes.label} variant='body2'>
              {label(t(...(action_library_messages.instance.summary_dialog.reported_to as [string, string])))}
            </Typography>
            <Typography className={classes.value} variant='body2'>
              {values.reportedTo || '-'}
            </Typography>
          </div>
          <div className={classes.row}>
            <Typography className={classes.label} variant='body2'>
              {label(t(...(action_library_messages.instance.summary_dialog.criticality as [string, string])))}
            </Typography>
            <Typography className={classes.value} variant='body2'>
              {values.criticality
                ? t(
                    action_library_messages.criticality[
                      values.criticality.toUpperCase() as keyof typeof action_library_messages.criticality
                    ],
                  )
                : '-'}
            </Typography>
          </div>
        </Box>

        <Box className={classes.section}>
          <Typography className={classes.sectionTitle} variant='subtitle1'>
            {t(action_library_messages.steps.instruction)}
          </Typography>
          <Typography dangerouslySetInnerHTML={{ __html: template?.shortDescription || '-' }} variant='body2' />
        </Box>

        <Box className={classes.section}>
          <Typography className={classes.sectionTitle} variant='subtitle1'>
            {t(...(action_library_messages.instance.summary_dialog.time_parameters as [string, string]))}
          </Typography>
          <div className={classes.row}>
            <Typography className={classes.label} variant='body2'>
              {label(t(...(action_library_messages.instance.summary_dialog.deadline as [string, string])))}
            </Typography>
            <Typography className={classes.value} variant='body2'>
              {values.deadlineDate ? formatDate(values.deadlineDate) : '-'}
            </Typography>
          </div>
          <div className={classes.row}>
            <Typography className={classes.label} variant='body2'>
              {label(t(...(action_library_messages.instance.summary_dialog.reminder as [string, string])))}
            </Typography>
            <Typography className={classes.value} variant='body2'>
              {values.reminderDate ? formatDate(values.reminderDate) : '-'}
            </Typography>
          </div>
          <div className={classes.row}>
            <Typography className={classes.label} variant='body2'>
              {label(t(...(action_library_messages.instance.summary_dialog.validity_period as [string, string])))}
            </Typography>
            <Typography className={classes.value} variant='body2'>
              {values.validityPeriodMonths != null
                ? `${values.validityPeriodMonths} ${
                    values.validityPeriodMonths === 1
                      ? t(...(action_library_messages.instance.form.month as [string, string]))
                      : t(...(action_library_messages.instance.form.months as [string, string]))
                  }`
                : t(...(action_library_messages.instance.form.validity_period_not_set as [string, string]))}
            </Typography>
          </div>
        </Box>

        <Box className={classes.section}>
          <Box alignItems='center' display='flex' mb={1}>
            <Typography className={classes.sectionTitle} style={{ marginBottom: 0, marginRight: 8 }} variant='subtitle1'>
              {t(...(action_library_messages.instance.summary_dialog.special_instructions as [string, string]))}
            </Typography>
          </Box>
          <Typography component='div' variant='body2'>
            {values.specialInstruction ? <RichTextContent content={values.specialInstruction} /> : '-'}
          </Typography>
        </Box>

        <Box className={classes.section}>
          <Typography className={classes.sectionTitle} variant='subtitle1'>
            {t(...(action_library_messages.instance.summary_dialog.steps as [string, string]))} ({includedSteps.length})
          </Typography>
          {includedSteps.length > 0 ? (
            <ul className={classes.assetList}>
              {includedSteps.map(step => (
                <li key={step.id} style={{ marginBottom: 8 }}>
                  <strong>{step.title}</strong> <strong>({getStepStatus(step.id, step.mandatory)})</strong>
                  {step.shortDescription && (
                    <div style={{ marginLeft: 16, marginTop: 4, fontSize: '0.875rem', color: 'rgba(0, 0, 0, 0.6)' }}>
                      <RichTextContent content={step.shortDescription} />
                    </div>
                  )}
                  {step.criticalityDisplay && (
                    <div style={{ marginLeft: 16, marginTop: 2, fontSize: '0.75rem', color: 'rgba(0, 0, 0, 0.5)' }}>
                      Criticality: {step.criticalityDisplay}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <Typography color='textSecondary' variant='body2'>
              No steps defined
            </Typography>
          )}
        </Box>

        <Box className={classes.section}>
          <Typography className={classes.sectionTitle} variant='subtitle1'>
            {t(...(action_library_messages.instance.summary_dialog.selected_assets as [string, string]))} ({values.selectedAssets.length})
          </Typography>
          {values.selectedAssets.length > 0 ? (
            <ul className={classes.assetList}>
              {values.selectedAssets.map(asset => (
                <li key={asset.id} style={{ marginBottom: 8 }}>
                  <strong>{asset.name}</strong>
                  <div style={{ marginLeft: 16, marginTop: 4, fontSize: '0.875rem' }}>
                    {t(...(action_library_messages.instance.summary_dialog.owner_label as [string, string]))}:{' '}
                    {asset.owner?.fullName || t(...(action_library_messages.instance.summary_dialog.not_available as [string, string]))}
                  </div>
                  {asset.owner?.email && (
                    <div style={{ marginLeft: 16, marginTop: 2, fontSize: '0.75rem', color: 'rgba(0, 0, 0, 0.6)' }}>
                      Email: {asset.owner.email}
                    </div>
                  )}
                  {asset.category?.name && (
                    <div style={{ marginLeft: 16, marginTop: 2, fontSize: '0.75rem', color: 'rgba(0, 0, 0, 0.6)' }}>
                      {t(action_library_messages.instance.form.asset_categories)}: {asset.category.name}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <Typography color='textSecondary' variant='body2'>
              {t(...(action_library_messages.instance.summary_dialog.no_assets_selected as [string, string]))}
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button disabled={isSubmitting} onClick={onClose}>
          {t(...(action_library_messages.instance.summary_dialog.cancel as [string, string]))}
        </Button>
        <Button color='primary' disabled={isSubmitting} onClick={onConfirm} variant='contained'>
          {isSubmitting
            ? t(...(action_library_messages.instance.summary_dialog.creating as [string, string]))
            : t(...(action_library_messages.instance.summary_dialog.create_instance as [string, string]))}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ActionLibraryInstanceSummaryDialog;
